import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, inject, ComputedRef } from "vue";
import router from "@/router";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  RouterName,
  ShowErrorModal,
  SignupFieldKey,
} from "@/types";
import { useUserStore } from "@/stores/userStore";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import DynamicSignupForm from "@/components/signup/DynamicSignupForm.vue";
import { RankCode } from "@/types";
import { useRoute } from "vue-router";
import { useServiceStore } from "@/stores/serviceStore";
import useGetSessionStorageItem from "@/use/useGetSessionStorageItem";
import { SESSION_KEY_RANK_CODE } from "@/types";
import { ISignupInfo } from "@/domain/entities/serviceInfo/serviceInfo.types";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";


const __default__ = {
  name: "SignUp",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const userStore = useUserStore();
const serviceStore = useServiceStore();
const route = useRoute();

const signupInfos: ComputedRef<ISignupInfo[]> = computed(
  () => serviceStore.signupInfos ?? []
);

const scrollContainerRef = ref<HTMLElement>();

const rankCode = computed(() => {
  if (signupInfos.value.length === 1) {
    return signupInfos.value?.[0]?.useRankCode;
  } else {
    return useGetSessionStorageItem<RankCode>(SESSION_KEY_RANK_CODE);
  }
});
const maskingUsername = route.query.maskingUsername?.toString() ?? "";
const maskingUserphone = route.query.maskingUserphone?.toString() ?? "";
const loginId = route.query.loginId?.toString() ?? "";
const name = route.query.name?.toString() ?? "";
const eziokCertAuthValueEnc =
  route.query.eziokCertAuthValueEnc?.toString() ?? "";
const dynamicSignupForm = ref<InstanceType<typeof DynamicSignupForm>>();

const userId = ref("");
const password = ref("");

const isManagerCertRequired = computed(
  () => !!serviceStore.serviceInfo?.isManagerCertRequired
);
const isPublicLineApi = computed(
  () => !!serviceStore.serviceInfo?.isPublicLineApi
);
const isPublicTalkApi = computed(
  () => !!serviceStore.serviceInfo?.isPublicTalkApi
);

const shouldShowCompleteSignUp = ref(false);
const toggleShowCompleteSignUp = (show: boolean) => {
  shouldShowCompleteSignUp.value = show;
};

const getCompleteSignUpText = computed(() =>
  isManagerCertRequired.value
    ? "회원가입 신청이 완료되었습니다. 담당자 승인 후 이용가능합니다."
    : "회원가입이 완료되었습니다. 지금 바로 통근버스 서비스를 이용해보세요!"
);

const isSigningUp = ref(false);
const requestSignup = async (formValues: Record<string, any>) => {
  if (isSigningUp.value || !rankCode.value) return;

  userId.value = formValues.loginId;
  password.value = formValues.password;
  formValues.eziokCertAuthValueEnc = eziokCertAuthValueEnc;

  isSigningUp.value = true;
  const signUpResult = await userStore.signUp(rankCode.value, formValues);
  isSigningUp.value = false;

  if (!signUpResult.isSuccess) {
    showErrorModal(
      "회원가입 오류",
      signUpResult.errorMessage,
      signUpResult.resultCode,
      signUpResult.errorTime
    );
    return;
  }

  toggleShowCompleteSignUp(true);
};

const onClickConfirmSignUp = async () => {
  toggleShowCompleteSignUp(false);
  isManagerCertRequired.value ? goToEntryPage() : login();
};

const login = async () => {
  const isAutoLogin = false;
  const loginResult = await userStore.login(
    userId.value,
    password.value,
    isAutoLogin
  );

  if (!loginResult.isSuccess) {
    showErrorModal(
      "로그인 오류",
      loginResult.errorMessage,
      loginResult.resultCode,
      loginResult.errorTime
    );
    return;
  }

  goToMainLogin();
};

/** 페이지 이동 */
const goToEntryPage = () => {
  isPublicLineApi.value || isPublicTalkApi.value
    ? goToMainLogout()
    : goToLogin();
};
const goToMainLogout = () => {
  router.push({ name: RouterName.MainLogout });
};

const goToLogin = () => {
  router.push({ name: RouterName.Login });
};

const goToMainLogin = () => {
  router.push({ name: RouterName.MainLogin });
};

// formValue에 회원가입 정보(이름, 휴대폰번호, 사원번호) 초기값 설정하기
const initSignupFormValue = () => {
  if (!dynamicSignupForm.value) return;

  dynamicSignupForm.value.updateFormValueWithFieldKey(
    SignupFieldKey.NAME,
    maskingUsername
  );
  dynamicSignupForm.value.updateFormValueWithFieldKey(
    SignupFieldKey.PHONE,
    maskingUserphone
  );
  dynamicSignupForm.value.updateFormValueWithFieldKey(
    SignupFieldKey.LOGIN_ID,
    loginId
  );

  dynamicSignupForm.value.updateFormValueWithFieldKey(
    SignupFieldKey.NICKNAME,
    name
  );
};

const initSignupFields = (rankCode?: string | null) => {
  if (!rankCode) return;
  serviceStore.initSignupFields(rankCode);
};

const goToBack = () => {
  route.meta.willGoBack = true;
  router.back();
};

const validateRankCode = (rankCode?: string | null) => {
  if (rankCode?.length) return;

  showErrorModal("회원가입 오류", "회원 유형을 확인할 수 없습니다.");
  goToBack();
};

const validateAuth = () => {
  if (maskingUsername.length && maskingUserphone.length && loginId) return;

  showErrorModal(
    "회원가입 오류",
    "본인인증 후 회원가입 하실 수 있습니다.",
    undefined,
    undefined,
    goToBack
  );
};

onMounted(() => {
  validateRankCode(rankCode.value);
  validateAuth();
  initSignupFields(rankCode.value);
  initSignupFormValue();
});

const __returned__ = { showErrorModal, userStore, serviceStore, route, signupInfos, scrollContainerRef, rankCode, maskingUsername, maskingUserphone, loginId, name, eziokCertAuthValueEnc, dynamicSignupForm, userId, password, isManagerCertRequired, isPublicLineApi, isPublicTalkApi, shouldShowCompleteSignUp, toggleShowCompleteSignUp, getCompleteSignUpText, isSigningUp, requestSignup, onClickConfirmSignUp, login, goToEntryPage, goToMainLogout, goToLogin, goToMainLogin, initSignupFormValue, initSignupFields, goToBack, validateRankCode, validateAuth, Page, NavigationBar, Modal, DynamicSignupForm, get RankCode() { return RankCode }, ScrollTopControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})